/* eslint-disable sonarjs/cognitive-complexity */
import { color, shadow } from '@ahamove/tokens';
import {
  createUserPlaceFn,
  deleteUserPlaceFn,
  updateUserPlaceFn,
  type Address,
} from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import { toast } from '@ahm/react-ui';
import {
  FloatingLabelInput,
  FloatingLabelTextArea,
} from '@ahm/react-ui/components/floating-label-fields';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Title,
  rem,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { BaseAddressAutocomplete } from '../autocomplete';
import { type Locales, createTranslations, locales } from '../locales';
import { AutocompleteModalId } from '../modals/constants';
import FindAddressModal from '../modals/find-address.modal';
import { genBuildFields } from '../utils';
import { useGetUserPlace } from './use-get-user-place';
import useUserPlaceSchema from './use-user-place.schema';
import IconTrash from '@/assets/icons/trash-red-icon.svg';

type SavedPlaceFormProps = {
  lang?: keyof Locales;
  initData?: Address;
  isNewSavePlace?: boolean;
};

export default function SavedPlaceForm({
  lang = 'vi',
  initData,
  isNewSavePlace = true,
}: SavedPlaceFormProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const t = createTranslations({ locales, lang });

  const [address, setAddress] = useState<Address | null>(initData ?? null);
  const [loading, setLoading] = useState<boolean>(false);

  const schema = useUserPlaceSchema(lang);
  const { refetch } = useGetUserPlace({ enabled: false });
  const setSavedLocations = useLocationStore.use.setSavedLocations();

  const [opened, { open, close }] = useDisclosure(false);

  type FormData = z.infer<typeof schema>;
  const formId = 'new-saved-place';

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    trigger,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      name: initData?.name ?? '',
      address: initData?.address ?? '',
      address_detail: initData?.address_detail ?? '',
      contact_name: initData?.contact_name ?? '',
      contact_number: initData?.contact_number ?? '',
      lat: initData?.lat ?? 0,
      lng: initData?.lng ?? 0,
    },
  });

  const handleSelectAddress = async (address: Address | null) => {
    if (address?.lat && address.lng) {
      setValue('lat', address.lat);
      setValue('lng', address.lng);
      setValue('address', address.address);
      setAddress(address);
    } else {
      setValue('lat', 0);
      setValue('lng', 0);
      setValue('address', '');
      setAddress(null);
    }

    await trigger(['lat', 'lng', 'address']);
  };

  const handleRemoveSavedPlace = async () => {
    try {
      await deleteUserPlaceFn(initData?.code ?? '');
      toast.success({
        title: 'Đã xóa địa chỉ.',
        withIcon: true,
        withCloseButton: false,
      });
      const { data } = await refetch();
      if (data) {
        setSavedLocations(data);
      }
      handleClose();
    } catch (error) {
      console.error('Error deleting saved place:', error);
    }
  };

  const onSubmit = async (values: FormData) => {
    const formData = {
      ...values,
      type: 'favorite_location',
      location: [values.lng, values.lat],
    };

    const payload = genBuildFields(formData, [
      'address',
      'type',
      'location',
      'name',
      'address_detail',
      'contact_name',
      'contact_number',
    ]);
    setLoading(true);
    try {
      if (initData && !isNewSavePlace) {
        payload.code = initData?.code ?? undefined;
        await updateUserPlaceFn(payload);
        toast.success({
          title: 'Đã cập nhât địa chỉ.',
          withIcon: true,
          withCloseButton: false,
        });
      } else {
        await createUserPlaceFn(payload);
        toast.success({
          title: 'Đã lưu địa chỉ.',
          withIcon: true,
          withCloseButton: false,
        });
      }
      const { data } = await refetch();
      if (data) {
        setSavedLocations(data);
      }
      handleClose();
    } catch (error) {
      toast.error({
        title: error?.response?.data?.title || 'Error',
        message: error?.response?.data?.message || 'Something went wrong',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    modals.closeAll();
  };

  const [openedModal, { open: openModal, close: closeModal }] =
    useDisclosure(false);

  return (
    <>
      <Stack
        bg={'neutral.0'}
        h={isMobile ? '100svh' : rem(600)}
        style={{ overflowX: 'hidden', overflowY: 'auto' }}
        gap={0}
      >
        {/* TODO: Modal header  */}
        <Group
          pos={'relative'}
          w="100%"
          px={rem(8)}
          py={0}
          bg="white"
          mih={46}
          style={(theme) => ({
            zIndex: 11,
            border: `1px solid ${theme.colors.neutral[0]}`,
            textAlign: 'center',
            borderBottom: `1px solid rgb(242, 244, 248)`,
          })}
        >
          <Title c="neutral.9" w={'100%'} order={4}>
            {isNewSavePlace ? t('new_save_place') : ' Cập nhật địa điểm đã lưu'}
          </Title>
          <ActionIcon
            size="lg"
            variant="subtle"
            color="neutral"
            radius="xl"
            style={{
              outline: 'none !important',
              position: 'absolute',
              top: '50%',
              left: rem(8),
              transform: `translateY(-50%)`,
            }}
            onClick={() => modals.close(AutocompleteModalId.NEW_SAVED_PLACED)}
          >
            <ArrowLeft size={24} color={color.neutral['70']} />
          </ActionIcon>
        </Group>

        {/* TODO: Modal Body  */}
        <Stack
          h={isMobile ? '100svh' : 'auto'}
          mah={isMobile ? '100svh' : 'auto'}
          style={{ overflow: 'auto' }}
          gap={12}
          flex={1}
        >
          <Stack
            flex={1}
            component={'form'}
            id={formId}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Stack bg="white" p={rem(16)}>
              <Controller
                name="name"
                control={control}
                disabled={loading}
                render={({ field: { value, onChange } }) => (
                  <FloatingLabelInput
                    withAsterisk
                    autoComplete="name"
                    label={t('place_name')}
                    placeholder={t('place_name_des')}
                    value={value}
                    onChange={(value) => onChange(value)}
                    error={errors.name?.message}
                  />
                )}
              />

              {isMobile ? (
                <>
                  <Button
                    color="neutral.3"
                    c="neutral.5"
                    variant="outline"
                    h={rem(56)}
                    fw={500}
                    justify="space-between"
                    rightSection={<ChevronRight />}
                    onClick={openModal}
                  >
                    {address ? address.address : t('address')}
                  </Button>
                </>
              ) : (
                <Controller
                  name="address"
                  control={control}
                  disabled={loading}
                  render={({ field: { onChange } }) => (
                    <BaseAddressAutocomplete
                      isFloatLabel
                      withinPortal={false}
                      lang={lang}
                      address={address}
                      label={t('address')}
                      placeholder={t('address')}
                      savedPlaceSlots={undefined}
                      onChange={async (e) => {
                        onChange(e?.address);
                        await handleSelectAddress(e);
                      }}
                      disabled={loading}
                      inputProps={{
                        error: errors.address?.message,
                        errorProps: { style: { fontSize: 12 } },
                        withAsterisk: true,
                      }}
                      inputStyles={{
                        label: {
                          fontSize: 14,
                        },
                        input: {
                          fontSize: 12,
                          border: `calc(0.0625rem* var(--mantine-scale)) solid #ced4da`,
                          height: rem(56),
                        },
                      }}
                    />
                  )}
                />
              )}

              <Controller
                name="address_detail"
                control={control}
                disabled={loading}
                render={({ field: { value, onChange } }) => (
                  <FloatingLabelTextArea
                    resize="vertical"
                    maxLength={255}
                    autoComplete="address_detail"
                    label={t('address_detail')}
                    placeholder={t('address_detail_des')}
                    value={value}
                    onChange={(value) => onChange(value)}
                  />
                )}
              />
            </Stack>

            <Stack bg="white" p={rem(16)}>
              <Controller
                name="contact_name"
                control={control}
                disabled={loading}
                render={({ field: { value, onChange } }) => (
                  <FloatingLabelInput
                    autoComplete="contact_name"
                    label={t('contact_name')}
                    placeholder={t('contact_name_des')}
                    value={value}
                    onChange={(value) => onChange(value)}
                  />
                )}
              />
              <Controller
                name="contact_number"
                control={control}
                disabled={loading}
                render={({ field: { value, onChange } }) => (
                  <FloatingLabelInput
                    autoComplete="contact_number"
                    label={t('contact_number')}
                    placeholder={t('contact_number_des')}
                    value={value}
                    onChange={(value) => onChange(value)}
                    error={errors.contact_number?.message}
                  />
                )}
              />
              {isNewSavePlace ? null : (
                <>
                  <Button
                    w="fit-content"
                    size="compact-md"
                    c="red.6"
                    variant="subtle"
                    radius={rem(4)}
                    fw={700}
                    onClick={open}
                    leftSection={<IconTrash size={24} />}
                    ml={-10}
                  >
                    Xóa địa chỉ
                  </Button>
                </>
              )}
            </Stack>
          </Stack>

          <Stack
            bg="white"
            p={rem(16)}
            mt="auto"
            style={{ boxShadow: shadow.buttonDock }}
          >
            {!isNewSavePlace ? (
              <Button
                w={'100%'}
                h={rem(48)}
                loading={loading}
                disabled={!isValid}
                type="submit"
                form={formId}
              >
                Cập nhật
              </Button>
            ) : (
              <Button
                w={'100%'}
                h={rem(48)}
                loading={loading}
                disabled={!isValid}
                type="submit"
                form={formId}
              >
                {t('submit_place')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Modal
        opened={openedModal}
        onClose={closeModal}
        withCloseButton={!isMobile}
        fullScreen
        closeOnEscape={false}
        zIndex={201}
        p={0}
        styles={{
          content: {
            backgroundColor: 'var(--mantine-color-neutral-0)',
          },
          body: { padding: 0 },
        }}
      >
        <FindAddressModal
          innerProps={{ lang }}
          onClose={closeModal}
          onChangeAddress={handleSelectAddress}
        />
      </Modal>

      {initData && (
        <Modal
          zIndex={500}
          opened={opened}
          onClose={close}
          withCloseButton={false}
          centered
          styles={{
            header: {
              textAlign: 'center',
            },
            body: { padding: 16 },
          }}
        >
          <Group
            pos={'relative'}
            w="100%"
            px={rem(8)}
            py={0}
            bg="white"
            mih={46}
            style={{
              textAlign: 'center',
            }}
          >
            <Title c="neutral.7" w={'100%'} order={5}>
              Xóa địa chỉ
            </Title>
          </Group>
          Bạn có chắc muốn xóa <strong>{initData.name}</strong> khỏi danh sách
          địa điểm đã lưu?
          <Group
            p={isMobile ? 12 : 0}
            gap={8}
            pos={{ base: 'fixed', sm: 'sticky' }}
            bottom={0}
            left={0}
            w="100%"
            bg="white"
            styles={{
              root: {
                zIndex: 100,
              },
            }}
            grow
          >
            <Button
              fullWidth
              c={'secondary.7'}
              variant={'light'}
              color={'neutral'}
              mih={rem(48)}
              radius={rem(4)}
              fw={700}
              onClick={close}
              type="button"
            >
              Hủy
            </Button>
            <Button
              onClick={handleRemoveSavedPlace}
              size="md"
              mih={rem(48)}
              w="50%"
            >
              Xóa
            </Button>
          </Group>
        </Modal>
      )}
    </>
  );
}
